import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  Button,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  List,
  ListItem,
  Divider,
  Grid,
  GridItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';
import PolicyResultsTable from './PolicyResultsTable';
import { AppContext } from '../AppContext';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const ContentExplorer = () => {
  const { accessToken } = React.useContext(AppContext);
  const [cptCodes, setCptCodes] = useState('');
  const [state, setState] = useState('TX');
  const [payerType, setPayerType] = useState('cms');
  const [useAdvancedLogic, setUseAdvancedLogic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [cachedLogic, setCachedLogic] = useState([]);
  const [loadingCache, setLoadingCache] = useState(false);

  
  const fetchCachedLogic = async () => {
    if (!accessToken) return;

    setLoadingCache(true);
    try {
      const response = await callApi('/api/v2/content-explorer/cached-logic', accessToken, {
        method: 'GET'
      });
      const data = await response.json();
      if (response.ok && data.status === 'success') {
        setCachedLogic(data.data);
      }
    } catch (err) {
      console.error("Error fetching cached logic:", err);
    } finally {
      setLoadingCache(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchCachedLogic();
    }
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cptCodes || !state) {
      setError('Please enter CPT codes and select a state.');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const cptCodesList = cptCodes.split(',').map(code => code.trim()).filter(code => code);

      const payload = {
        cpt_codes: cptCodesList,
        state: state,
        payer_type: payerType,
        use_advanced_logic: useAdvancedLogic
      };

      const response = await callApi('/api/v2/content-explorer/analyze', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok && data.status === 'success') {
        setResults(data.data);
      } else {
        setError(data.message || 'An error occurred during analysis.');
      }
    } catch (err) {
      console.error("Error calling content explorer API:", err);
      setError(err.message || 'Failed to connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  const renderMappingDetails = () => {
    if (!results || !results.mapping_details) return null;
    const { mac_name, contractor_ids } = results.mapping_details;

    return (
      <Card mb={6} variant="outline">
        <CardHeader bg="gray.50" py={3}>
          <Heading size="sm">State to MAC Mapping</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <Text fontWeight="bold" display="inline">State: </Text>
              <Text display="inline">{results.inputs.state}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="bold" display="inline">MAC Name: </Text>
              <Text display="inline">{mac_name}</Text>
            </GridItem>
            <GridItem colSpan={2}>
              <Text fontWeight="bold" mb={2}>Active Contractor IDs:</Text>
              <HStack spacing={2}>
                {contractor_ids && contractor_ids.length > 0 ? (
                  contractor_ids.map(id => <Badge colorScheme="blue" key={id}>{id}</Badge>)
                ) : (
                  <Text color="gray.500">None found</Text>
                )}
              </HStack>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>
    );
  };

  const renderPolicies = (cptCode) => {
    const policyData = results.relevant_policies[cptCode];
    if (!policyData) return <Text>No policy data available for {cptCode}</Text>;

    return (
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>
            Primary Search <Badge colorScheme="green" ml={2}>State Specific</Badge>
          </Tab>
          {policyData.fallback_search && (
            <Tab>
              Fallback Search <Badge colorScheme="orange" ml={2}>National</Badge>
            </Tab>
          )}
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Heading size="sm" mb={3}>Local Coverage Determinations (LCDs)</Heading>
                <PolicyResultsTable policies={policyData.primary_search?.lcds} type="LCDs" />
              </Box>
              <Box>
                <Heading size="sm" mb={3}>Articles</Heading>
                <PolicyResultsTable policies={policyData.primary_search?.articles} type="Articles" />
              </Box>
              <Box>
                <Heading size="sm" mb={3}>National Coverage Determinations (NCDs)</Heading>
                <PolicyResultsTable policies={policyData.primary_search?.ncds} type="NCDs" />
              </Box>
            </VStack>
          </TabPanel>

          {policyData.fallback_search && (
            <TabPanel px={0}>
              <Alert status="warning" mb={4} borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Fallback Search Triggered</AlertTitle>
                  <AlertDescription>
                    No high-quality state-specific policies were found, so a national search was performed.
                  </AlertDescription>
                </Box>
              </Alert>
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Heading size="sm" mb={3}>Local Coverage Determinations (LCDs)</Heading>
                  <PolicyResultsTable policies={policyData.fallback_search?.lcds} type="LCDs" />
                </Box>
                <Box>
                  <Heading size="sm" mb={3}>Articles</Heading>
                  <PolicyResultsTable policies={policyData.fallback_search?.articles} type="Articles" />
                </Box>
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    );
  };

  const renderCoveredIcd10s = (cptCode) => {
    const icd10Data = results.covered_icd10s[cptCode];
    if (!icd10Data) return <Text>No ICD-10 data available for {cptCode}</Text>;

    let statusColor = 'gray';
    if (icd10Data.status === 'primary_contractor_coverage_found') statusColor = 'green';
    else if (icd10Data.status === 'national_or_other_contractor_guidance_found') statusColor = 'orange';
    else if (icd10Data.status === 'no_coverage_found') statusColor = 'red';

    if (results.inputs.use_advanced_logic) {
      return (
        <Box>
          <HStack mb={4}>
            <Text fontWeight="bold">Search Status: </Text>
            <Badge colorScheme={statusColor}>{icd10Data.status.replace(/_/g, ' ').toUpperCase()}</Badge>
          </HStack>
          
          {icd10Data.advanced_logic && icd10Data.advanced_logic.length > 0 ? (
            <VStack spacing={4} align="stretch">
              {icd10Data.advanced_logic.map((logic, index) => (
                <Card key={index} size="sm" variant="outline">
                  <CardHeader bg="gray.50">
                    <HStack justify="space-between">
                      <Heading size="xs">Article: {logic.article_id} (v{logic.article_version})</Heading>
                      <Badge colorScheme={logic.generation_status === 'SUCCESS' ? 'green' : 'red'}>
                        {logic.generation_status || 'SUCCESS'}
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      {logic.valid_combinations && logic.valid_combinations.map((combo, cIndex) => (
                        <Box key={cIndex} p={3} bg="blue.50" borderRadius="md">
                          <Text fontWeight="bold" mb={2}>{combo.combination_description}</Text>
                          <Accordion allowMultiple>
                            {combo.required_groups && combo.required_groups.map((group, gIndex) => (
                              <AccordionItem key={gIndex} border="1px solid" borderColor="blue.200" borderRadius="md" mb={2} bg="white">
                                <h2>
                                  <AccordionButton _expanded={{ bg: 'blue.50' }}>
                                    <Box flex="1" textAlign="left" fontWeight="bold">
                                      Group {group.group_id} 
                                      <Badge ml={2} colorScheme="blue">{group.valid_codes ? group.valid_codes.length : 0} codes</Badge>
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4} maxHeight="300px" overflowY="auto" bg="gray.50">
                                  {group.valid_codes && group.valid_codes.length > 0 ? (
                                    <List spacing={2}>
                                      {group.valid_codes.map((codeItem, idx) => (
                                        <ListItem key={idx} fontSize="sm">
                                          <Text as="span" fontWeight="bold">{codeItem.code}</Text> - {codeItem.description}
                                        </ListItem>
                                      ))}
                                    </List>
                                  ) : (
                                    <Text fontSize="sm" color="gray.500">No codes found in this group.</Text>
                                  )}
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          ) : (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              No advanced logic found for this CPT code.
            </Alert>
          )}
        </Box>
      );
    }

    // Group codes by source article to show counts instead of the full list (Legacy Flat List)
    const groupedCodes = {};
    if (icd10Data.covered_codes) {
      icd10Data.covered_codes.forEach(item => {
        const source = item.source_article || 'Unknown Source';
        if (!groupedCodes[source]) {
          groupedCodes[source] = 0;
        }
        groupedCodes[source]++;
      });
    }

    const sourceEntries = Object.entries(groupedCodes);

    return (
      <Box>
        <HStack mb={4}>
          <Text fontWeight="bold">Search Status: </Text>
          <Badge colorScheme={statusColor}>{icd10Data.status.replace(/_/g, ' ').toUpperCase()}</Badge>
        </HStack>
        
        {sourceEntries.length > 0 ? (
          <List spacing={3} border="1px" borderColor="gray.200" borderRadius="md" p={4}>
            {sourceEntries.map(([source, count], index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <Grid templateColumns="1fr auto" gap={4} alignItems="center">
                    <Text fontWeight="bold">Source Article: {source}</Text>
                    <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>{count} Covered ICD-10 Codes</Badge>
                  </Grid>
                </ListItem>
                {index < sourceEntries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            No covered ICD-10 codes found for this CPT code.
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack align="stretch" spacing={6}>
          <Box>
            <Heading size="lg" mb={2}>Content Explorer</Heading>
            <Text color="gray.600">
              Inspect how the system maps CPT codes and states to CMS coverage policies and diagnosis codes.
            </Text>
          </Box>

          <Card variant="outline">
            <CardBody>
              <form onSubmit={handleSubmit}>
                <Grid templateColumns="repeat(12, 1fr)" gap={6} alignItems="end">
                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl isRequired>
                      <FormLabel>CPT Codes (comma-separated)</FormLabel>
                      <Input 
                        placeholder="e.g., 95940, 95865" 
                        value={cptCodes}
                        onChange={(e) => setCptCodes(e.target.value)}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 12, md: 3 }}>
                    <FormControl isRequired>
                      <FormLabel>State</FormLabel>
                      <Select 
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      >
                        {US_STATES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                                    <GridItem colSpan={{ base: 12, md: 3 }}>
                    <FormControl>
                      <FormLabel>Payer Type</FormLabel>
                      <Select value={payerType} isDisabled>
                        <option value="cms">CMS (Medicare)</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={12}>
                    <Checkbox 
                      isChecked={useAdvancedLogic} 
                      onChange={(e) => setUseAdvancedLogic(e.target.checked)}
                      colorScheme="blue"
                    >
                      Use Advanced Group Logic (Slower)
                    </Checkbox>
                  </GridItem>
                  <GridItem colSpan={12}>
                    <Button 
                      type="submit" 
                      colorScheme="blue" 
                      isLoading={loading}
                      loadingText="Analyzing..."
                    >
                      Explore Content
                    </Button>
                  </GridItem>
                </Grid>
              </form>
            </CardBody>
          </Card>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle mr={2}>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text mt={4} color="gray.600">Analyzing content mappings...</Text>
            </Box>
          )}

          
          <Card variant="outline" mt={8}>
            <CardHeader bg="gray.50" py={3}>
              <HStack justify="space-between">
                <Heading size="sm">Cached ICD-10 Group Logic</Heading>
                <Button size="sm" onClick={fetchCachedLogic} isLoading={loadingCache}>Refresh</Button>
              </HStack>
            </CardHeader>
            <CardBody>
              {cachedLogic.length > 0 ? (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>CPT Code</Th>
                        <Th>Article ID</Th>
                        <Th>Version</Th>
                        <Th>Status</Th>
                        <Th>Combinations</Th>
                        <Th>Total Codes</Th>
                        <Th>Logic Summary</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cachedLogic.map((item) => (
                        <Tr key={item.id}>
                          <Td fontWeight="bold">{item.cpt_code}</Td>
                          <Td>{item.article_id}</Td>
                          <Td>{item.article_version}</Td>
                          <Td>
                            <Badge colorScheme={item.generation_status === 'SUCCESS' ? 'green' : 'red'}>
                              {item.generation_status}
                            </Badge>
                          </Td>
                          <Td>{item.total_combinations}</Td>
                          <Td>{item.total_codes}</Td>
                          <Td>
                            <List spacing={1}>
                              {item.combination_summaries.map((summary, idx) => (
                                <ListItem key={idx} fontSize="xs" color="gray.600">
                                  • {summary}
                                </ListItem>
                              ))}
                            </List>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500">No cached logic found.</Text>
              )}
            </CardBody>
          </Card>

          {results && !loading && (
            <Box>
              {renderMappingDetails()}

              <Heading size="md" mt={8} mb={4}>Results by CPT Code</Heading>
              
              <Tabs variant="enclosed" colorScheme="blue">
                <TabList>
                  {results.inputs.cpt_codes.map(cpt => (
                    <Tab key={cpt}>{cpt}</Tab>
                  ))}
                </TabList>

                <TabPanels>
                  {results.inputs.cpt_codes.map(cpt => (
                    <TabPanel key={cpt} px={0} pt={6}>
                      <VStack align="stretch" spacing={6}>
                        <Card variant="outline">
                          <CardHeader bg="gray.50" py={3}>
                            <Heading size="sm">Relevant Policies</Heading>
                          </CardHeader>
                          <CardBody>
                            {renderPolicies(cpt)}
                          </CardBody>
                        </Card>

                        <Card variant="outline">
                          <CardHeader bg="gray.50" py={3}>
                            <Heading size="sm">Covered ICD-10 Codes</Heading>
                          </CardHeader>
                          <CardBody>
                            {renderCoveredIcd10s(cpt)}
                          </CardBody>
                        </Card>
                      </VStack>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default ContentExplorer;
